import type { Context, MutableRefObject } from 'react';
import {
  createContext,
  useState,
  useCallback,
  useMemo,
  useContext,
  useRef,
  useEffect,
} from 'react';
import { checkContext } from '@kiwicom/account-client-utils';

export type Focusable = {
  readonly current: null | {
    readonly focus: () => void;
    readonly getAttribute: (arg0: string) => unknown;
  };
};
type State = {
  readonly refs: Map<string, Focusable>;
  readonly pushRef: (ref: Focusable, key: string) => void;
};
const RefContext: Context<State> = createContext<State>(null);
type Props = {
  readonly children: JSX.Element | JSX.Element[];
};

/**
 * The idea of this component is to collect refs in a form. When there are errors, we can use this to
 * focus the element, to show the actual component that errored to the user
 */
const CollectRefsProvider = (props: Props): JSX.Element => {
  const [refs, setRef] = useState(new Map());
  const pushRef = useCallback((ref, key) => {
    setRef((refs) => {
      return new Map(refs.set(key, ref));
    });
  }, []);
  const state = useMemo(() => {
    return {
      refs,
      pushRef,
    };
  }, [pushRef, refs]);
  return <RefContext.Provider value={state}>{props.children}</RefContext.Provider>;
};

function useCollectRefs(): State {
  return checkContext(useContext(RefContext), 'CollectRefsProvider');
}

function usePushRef<T>(key: string): MutableRefObject<T> {
  const ref = useRef(null);
  const { pushRef } = useCollectRefs();
  useEffect(() => {
    if (ref.current != null) {
      pushRef(ref, key);
    }
  }, [key, pushRef]);
  return ref;
}

export { CollectRefsProvider, useCollectRefs, usePushRef };
